import { StoreBrief } from '../../data/types/stores';
import { AuthenticatedApiRepository } from '../../data/repositories/authenticated_base_repository';
import axios, { AxiosInstance } from 'axios';
import { solrAxiosInstance } from '../../utils/axios/axios';
import { ServerException } from '../../config/errors/server_exceptions';
import { toast } from 'react-toastify';
import { ImageType } from '../types/files';

import {
  SolrBuyerItemsResponse,
  SolrTemplateResponse,
} from '../../data/types/listings';
import { ConfigSelector } from '../../utils/axios/configutation';

export type SilalImagesResponse = {
  images: ImageType[];
  total: number;
  page: number;
  pages: number;
  per_page: number;
  http_status: number;
};
export type SellerFilesResponse = {
  files: ImageType[];
  total: number;
  page: number;
  pages: number;
  per_page: number;
  http_status: number;
};
class SolrApiRepository extends AuthenticatedApiRepository {
  private static instance: SolrApiRepository | null = null;
  constructor(axiosInstance: AxiosInstance) {
    super(axiosInstance);
  }

  public static getInstance(axiosInstance: AxiosInstance): SolrApiRepository {
    if (!SolrApiRepository.instance) {
      SolrApiRepository.instance = new SolrApiRepository(axiosInstance);
    }
    return SolrApiRepository.instance;
  }

  getStores = async (limit: number = 10000, offset: number = 0) => {
    try {
      const res = await axios.post(
        `${
          ConfigSelector.getConfig('/tmp').solrApiHost
        }/solr/silal_stores_core/select`,
        {
          query: '*',
          filter: ['listed:true'],
          fields: ['*'],
          limit: limit,
          offset: offset,
        },
      );
      if (
        res.status !== 200 &&
        res.status !== 201 &&
        res.status !== 202 &&
        res.status !== 203 &&
        res.status !== 204
      ) {
        throw new ServerException(res.data);
      }
      return res.data.response.docs as StoreBrief[];
    } catch (e: any) {
      toast.error(e.message);
      return false;
    }
  };

  getListingsOfCategoryAndSubcategories = async (category_id: string) => {
    try {
      const res = await axios.post(
        `${
          ConfigSelector.getConfig('/tmp').solrApiHost
        }/solr/silal_listings_core/select`,
        {
          query: '*',
          filter: ['is_silal_owned:false', `category_stack_ids:${category_id}`],
          fields: ['*'],
          params: {
            group: 'true',
            'group.limit': 1,
            'group.field': 'listing_id',
            'group.main': true,
          },
          limit: 100000,
        },
      );
      if (res.status !== 200) {
        throw new ServerException(res.data);
      }
      return res.data.response.docs;
    } catch (e: any) {
      toast.error(e.message);
      return false;
    }
  };

  getManagementTemplates = async () => {
    try {
      const res = await axios.post(
        `${
          ConfigSelector.getConfig('/tmp').solrApiHost
        }/solr/silal_templates_core/select`,
        {
          query: '*',
          filter: ['is_silal_owned:true'],
          fields: ['*'],
          offset: 0,
          limit: 100000,
        },
      );
      if (res.status !== 200) {
        throw new ServerException(res.data);
      }
      return res.data.response.docs as SolrTemplateResponse[];
    } catch (e: any) {
      toast.error(e.message);
      return false;
    }
  };

  searchManagementSilalOwnedTemplates = async ({
    query,
    sortField = 'created_at_dt',
    sortOrder = 'desc',
    limit = 100000,
    offset = 0,
  }: {
    query: string;
    sortField?: string;
    sortOrder?: string;
    limit?: number;
    offset?: number;
  }) => {
    try {
      const res = await axios.post(
        `${
          ConfigSelector.getConfig('/tmp').solrApiHost
        }/solr/silal_templates_core/search/json`,
        {
          query: query === '' ? '*' : query,
          filter: ['is_silal_owned:true'],
          fields: ['*'],
          offset: offset,
          limit: limit,
          sort: `${sortField} ${sortOrder}`,
        },
      );
      if (res.status !== 200) {
        throw new ServerException(res.data);
      }
      return res.data.response.docs as SolrTemplateResponse[];
    } catch (e: any) {
      toast.error(e.message);
      return false;
    }
  };

  searchBuyerQuery = async ({
    query,
    fields,
    offset = 0,
    limit = 100000,
    sortField = 'created_at_dt',
    sortOrder = 'desc',
    filter,
  }: {
    query?: string;
    fields?: string[];
    sortField?: string;
    sortOrder?: string;
    limit?: number;
    offset?: number;
    filter?: string[];
  }) => {
    try {
      const res = await axios.post(
        `${
          ConfigSelector.getConfig('/tmp').solrApiHost
        }/solr/silal_listings_core/buyer/search/json`,
        {
          query: query === '' ? '*' : query,
          fields: fields ?? ['*'],
          offset: offset,
          limit: limit,
          sort: `${sortField} ${sortOrder}`,
          filter: filter || [],
        },
      );
      if (res.status !== 200) {
        throw new ServerException(res.data);
      }
      return res.data.response.docs as SolrBuyerItemsResponse[];
    } catch (e: any) {
      toast.error(e.message);
      return false;
    }
  };

  getSilalImages = async ({
    page = 1,
    limit = 100,
    query = '*',
    sortField = 'date_created',
    sortOrder = 'desc',
    is_silal_owned,
    storeId,
  }: {
    page?: number;
    limit?: number;
    query?: string;
    sortField?: string;
    sortOrder?: string;
    is_silal_owned: boolean;
    storeId?: number;
  }): Promise<SilalImagesResponse | SellerFilesResponse | false> => {
    try {
      const offset = (page - 1) * limit;
      console.log('Page:', page, 'Limit:', limit, 'Offset:', offset);

      const requestBody = {
        query,
        filter: [
          'is_image:true',
          `is_silal_owned:${is_silal_owned}`,
          is_silal_owned === false ? `store_id:${storeId}` : '',
        ].filter(Boolean), // Remove empty strings
        fields: [
          'id',
          'original_name',
          'alt',
          'date_created',
          'date_uploaded',
          'version_id',
          'public_url',
        ],
        limit,
        offset,
        sort: `${sortField} ${sortOrder}`,
      };

      const res = await axios.post(
        `${ConfigSelector.getConfig('/tmp').solrApiHost}/solr/silal_images_core/images/search/json`,
        requestBody,
      );

      if (res.status !== 200) {
        throw new ServerException(res.data);
      }

      const response = res.data.response;
      if (!response || response.docs.length === 0) {
        console.warn('No more items found.');
        return {
          files: [],
          images: [],
          total: response.numFound,
          page,
          pages: Math.ceil((response.numFound || 0) / limit),
          per_page: limit,
          http_status: res.status,
        };
      }

      // Assign the proper property based on is_silal_owned
      const items: ImageType[] = response.docs.map((doc: any) => ({
        id: doc.id,
        is_public: doc.is_public || false,
        is_uploaded: doc.is_uploaded || false,
        is_image: doc.is_image || false,
        locked: doc.locked || false,
        ownership_type: doc.ownership_type,
        ownership_type_name: doc.ownership_type_name,
        extension: doc.extension,
        original_name: doc.original_name,
        alt: doc.alt,
        date_created: doc.date_created,
        date_uploaded: doc.date_uploaded,
        version_id: doc.version_id,
        public_s3_url: doc.public_url,
        get_object_presigned_url: doc.get_object_presigned_url || '',
      }));

      const result: any = {
        total: response.numFound,
        page,
        pages: Math.ceil(response.numFound / limit),
        per_page: limit,
        http_status: res.status,
      };

      // Dynamically add either `images` or `files` based on `is_silal_owned`
      if (is_silal_owned) {
        result.images = items; // Only add `images` if `is_silal_owned` is true
      } else {
        result.files = items; // Only add `files` if `is_silal_owned` is false
      }

      return result;
    } catch (error: any) {
      console.error('Error fetching Silal items:', error);
      toast.error(error.message);
      return false;
    }
  };
}
const SolrRepository = SolrApiRepository.getInstance(
  solrAxiosInstance as AxiosInstance,
);

export default SolrRepository;
